import { useState } from "react";
import { useChangePassword } from "../hooks/useSession.js";
import { Button, ErrorBanner, Field, Input, Modal } from "./ui.js";

/**
 * Self-service password change (issue #4). The server keeps this session
 * alive (fresh access + refresh tokens) and revokes every other session for
 * the account, so on success the user simply carries on.
 */
export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const changePassword = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const mismatch = confirm.length > 0 && confirm !== newPassword;
  const tooShort = newPassword.length > 0 && newPassword.length < 8;
  const unchanged = newPassword.length > 0 && newPassword === currentPassword;
  const blocked = mismatch || tooShort || unchanged || !currentPassword || !newPassword;
  const succeeded = changePassword.isSuccess;

  return (
    <Modal title="Change password" onClose={onClose}>
      {succeeded ? (
        <div className="space-y-4">
          <p className="text-sm text-emerald-300">
            Password updated. You stay signed in here; every other session for
            this account has been signed out.
          </p>
          <Button onClick={onClose}>Close</Button>
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (blocked) return;
            changePassword.mutate({ currentPassword, newPassword });
          }}
        >
          {changePassword.isError ? (
            <ErrorBanner message={(changePassword.error as Error).message} />
          ) : null}

          <Field label="Current password">
            <Input
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          </Field>

          <Field label="New password" hint="At least 8 characters.">
            <Input
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
            />
          </Field>
          {tooShort ? (
            <p className="text-xs text-rose-300">Passwords must be at least 8 characters.</p>
          ) : null}
          {unchanged ? (
            <p className="text-xs text-amber-300">
              The new password must be different from the current one.
            </p>
          ) : null}

          <Field label="Confirm new password">
            <Input
              type="password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>
          {mismatch ? (
            <p className="text-xs text-rose-300">Passwords do not match.</p>
          ) : null}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={blocked || changePassword.isPending}>
              {changePassword.isPending ? "Updating…" : "Update password"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
